import {v5 as uuidV5} from 'uuid';

import type {Nullable} from '../../../utils/utility-types';

import {logger} from '../../../utils/logger';
import {STORAGE_HEADER_NAME} from '../../../utils/storageToken';
import {type RemoteFileStorageService} from './RemoteFileStorage.service';

export class RemoteFileStorageUrl {
  private readonly DEFAULT_EXTENSION = 'm4a';
  private SIGN_KEY = 'sign';
  private STORAGE_URL_REGEX =
    /^https:\/\/(?:bucket|media)(?:\.[A-Za-z0-9-]+)*\.yourcoach\.health\/.*/;

  constructor(
    private readonly remoteFileStorageService: RemoteFileStorageService,
  ) {}

  checkIsExpired(url: string) {
    if (this.shouldRunGuard(url)) {
      try {
        const urlObj = new URL(url);
        const token = urlObj.searchParams.get(this.SIGN_KEY);

        if (!token) {
          // eslint-disable-next-line no-console
          console.warn(`No token found in URL: ${url}`);

          return false;
        }

        return this.remoteFileStorageService.token.checkIsExpired(token);
      } catch (error) {
        logger.error(`Invalid URL format: ${error}`);

        return false;
      }
    }

    return false;
  }

  checkIsStorage(url: string): boolean {
    return this.STORAGE_URL_REGEX.test(url);
  }

  computeExtension(url: string) {
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname;

      const lastSegment = pathname.split('/').pop() ?? '';
      const dotIdx = lastSegment.lastIndexOf('.');

      let ext: string | undefined;

      if (dotIdx > 0) {
        const candidate = lastSegment.slice(dotIdx + 1).toLowerCase();

        // Accept sane extensions only (alphanumeric).
        if (/^[a-z0-9]+$/.test(candidate)) {
          ext = candidate;
        }
      }

      if (ext) return ext;

      // B2B thumbs sometimes lack an extension
      if (pathname.includes('thumb') || pathname.includes('image')) {
        return 'jpeg';
      }

      return this.DEFAULT_EXTENSION;
    } catch (error) {
      logger.error(
        `RemoteFileStorageService.Url | Invalid URI: ${url} | Error: ${error.message}`,
      );

      return this.DEFAULT_EXTENSION;
    }
  }

  getClean(url: string) {
    if (this.shouldRunGuard(url)) {
      try {
        const urlObj = new URL(url);

        return urlObj.origin + urlObj.pathname;
      } catch (error) {
        logger.error(`Invalid URL format in getCleanUrl: ${error}`);

        return url;
      }
    }

    return url;
  }

  getHeaders(url: string) {
    if (this.shouldRunGuard(url)) {
      return {
        [STORAGE_HEADER_NAME]: this.remoteFileStorageService.token.value,
      };
    }
  }

  getId(url: string, ext?: Nullable<string>) {
    let urlToProcess = url;

    /**
     * If it's a storage URL, we need to remove search as it contains signature
     *
     * For the rest urls we pass it as is
     */
    if (this.checkIsStorage(url)) {
      try {
        const urlObj = new URL(url);

        urlToProcess = urlObj.origin + urlObj.pathname;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
          'Failed to process Storage URL (%s): %s',
          url,
          error.message,
        );
      }
    }

    const uuid = uuidV5(urlToProcess, this.ID_UUID_NAMESPACE).replace(/-/g, '');

    return `${uuid}.${ext ?? this.DEFAULT_EXTENSION}`;
  }

  sign(url: string) {
    if (this.shouldRunGuard(url)) {
      try {
        const urlObj = new URL(url);

        // Remove existing token if present
        urlObj.searchParams.delete(this.STORAGE_HEADER_NAME);

        // Add new token
        urlObj.searchParams.append(
          this.STORAGE_HEADER_NAME,
          this.remoteFileStorageService.token.value,
        );

        return urlObj.toString();
      } catch (error) {
        logger.error(`Failed to sign URL: ${error.message}`);

        return url;
      }
    }

    return url;
  }

  private get ID_UUID_NAMESPACE() {
    return '1ea96553-8f1d-4a07-b52a-210a245c9104';
  }

  private get STORAGE_HEADER_NAME() {
    return STORAGE_HEADER_NAME;
  }

  private shouldRunGuard(url: string) {
    return (
      this.remoteFileStorageService._shouldRunGuard() &&
      this.checkIsStorage(url)
    );
  }
}
